import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { TFunction } from 'react-i18next';

import { KBApi } from '../api';
import ArticleList from '../components/ArticleList';
import { Box } from '../components/generic/box/box';
import PageLoading from '../components/PageLoading';
import PageNav from '../components/PageNav';
import { parseArticle } from '../utils/parsing';
import { useDelayedFlag } from '../utils/useDelayedFlag';

import type { ShortArticle } from '../components/ArticleList';
import type { Crumb } from '../components/Breadcrumbs';
import type { PublicTag } from '../types';

interface Article {
  id: string;
  title: string;
  content: string;
  parents: ShortArticle[];
  children: ShortArticle[];
  section: PublicTag | null;
  topic: PublicTag | null;
}

// Loading carries the article already on screen, so navigating to the next one
// does not blank the page while the request is in flight.
function reducer(state: State, action: State): State {
  if (action.status === 'loading' && state.status === 'success') {
    return { status: 'loading', data: state.data };
  }

  return action;
}

const initState = { status: 'loading' };
type LoadingState = { status: 'loading'; data?: Article };
type ErrorState = { status: 'error'; error: string };
type SuccessState = { status: 'success'; data: Article };
type State = LoadingState | ErrorState | SuccessState;
export interface ArticleParams {
  hash?: string;
  id?: string;
}

async function fetchArticle(params: ArticleParams, dispatch: React.Dispatch<State>, t: TFunction) {
  if (!params.id || !params.hash) {
    return dispatch({ status: 'error', error: t('resource_nonexisting') });
  }
  return KBApi.getArticleById(params.hash, params.id)
    .then((response) => {
      dispatch({ status: 'success', data: response.data as Article });
    })
    .catch(() => {
      dispatch({ status: 'error', error: t('download_failed') });
    });
}

const renderSuccess = (state: SuccessState, t: TFunction, params: ArticleParams, isSlow = false) => {
  const anyRelatedArticles = state.data.children.length > 0 || state.data.parents.length > 0;
  const classNameForRelatedArticles = anyRelatedArticles ? 'col-span-3' : 'col-span-0 hidden';

  const crumbs: Crumb[] = [
    { label: t('breadcrumbs.home'), to: `/v/${params.hash}` },
    ...(state.data.section
      ? [{ label: state.data.section.name, to: `/v/${params.hash}/c/${state.data.section.id}` }]
      : []),
    ...(state.data.topic && state.data.section
      ? [
          {
            label: state.data.topic.name,
            to: `/v/${params.hash}/c/${state.data.section.id}/t/${state.data.topic.id}`
          }
        ]
      : []),
    { label: state.data.title }
  ];

  return (
    <div style={{ opacity: isSlow ? 0.6 : 1, transition: 'opacity 150ms' }}>
      <PageNav crumbs={crumbs} fallbackTo="../" />
      <div className="grid grid-cols-12 gap-2">
        <div className={classNameForRelatedArticles}>
          {state.data.parents.length > 0 && (
            <>
              <h3 className="pb-2 text-lg">{t('articles.parent')}</h3>
              <ArticleList viewHash={params.hash ?? ''} articles={state.data.parents} />
            </>
          )}
          {state.data.children.length > 0 && (
            <>
              <h3 className="pb-2 text-lg">{t('articles.child')}</h3>
              <ArticleList viewHash={params.hash ?? ''} articles={state.data.children} />
            </>
          )}
        </div>
        <div className="col-span-9">
          <Box as="h2" style={{ fontWeight: 'bold', marginTop: 0 }} className="pb-2 text-xl font-bold">
            {state.data.title}
          </Box>
          <div
            dangerouslySetInnerHTML={{
              // The article on screen is not always the one the route names:
              // the previous article stays up while the next one loads. Image
              // urls are built from the article id, so that id has to come from
              // the article being rendered rather than from the route.
              __html: parseArticle(state.data.content, { hash: params.hash, id: state.data.id })
            }}
          />
        </div>
      </div>
    </div>
  );
};

const renderError = (state: ErrorState, dispatch: React.Dispatch<State>, t: TFunction) => {
  return (
    <div>
      <h2>{t('error')}</h2>
      <p>{t('description')}:</p>
      <code>{state.error}</code>
      <button
        className="bg-grey-500 br-mg"
        onClick={() => {
          dispatch({ status: 'loading' });
        }}
      >
        {t('try_again')}
      </button>
    </div>
  );
};

export const ViewArticle = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initState as any);
  const params = useParams();

  const isSlow = useDelayedFlag(state.status === 'loading');

  useEffect(() => {
    fetchArticle({ hash: params.hash, id: params.id }, dispatch, t);
  }, [params.id]);

  switch (state.status) {
    case 'success': {
      return renderSuccess(state as SuccessState, t, params);
    }
    case 'loading': {
      const previous = (state as LoadingState).data;

      if (previous) {
        return renderSuccess({ status: 'success', data: previous }, t, params, isSlow);
      }

      return isSlow ? <PageLoading /> : null;
    }
    default:
      return renderError(state as ErrorState, dispatch, t);
  }
};
